import React, { useState, useEffect } from 'react';
import api from '../auth/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * ExerciseList Component
 * 
 * Displays a filterable table of exercises with edit/delete actions.
 * 
 * Props:
 * - onEdit: (exercise) => void - Callback when edit button is clicked
 * - onSelect: (exerciseIds: array) => void - Callback when exercises are selected
 * - selectable: boolean - Whether to show checkboxes for selection
 */
const ExerciseList = ({ onEdit, onSelect, selectable = false }) => {
    const [exercises, setExercises] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);

    // Filters
    const [filters, setFilters] = useState({
        language: 'all',
        category_id: 'all',
        sub_category_id: 'all',
        search: ''
    });

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        fetchExercises();
    }, [filters]);

    useEffect(() => {
        if (onSelect) {
            onSelect(selectedIds);
        }
    }, [selectedIds, onSelect]);

    const fetchCategories = async () => {
        try {
            const response = await api.get('/categories');
            setCategories(response.data);
        } catch (err) {
            console.error('Error fetching categories:', err);
        }
    };

    const fetchExercises = async () => {
        try {
            setLoading(true);
            const params = {};
            if (filters.language !== 'all') params.language = filters.language;
            if (filters.category_id !== 'all') params.category_id = filters.category_id;
            if (filters.sub_category_id !== 'all') params.sub_category_id = filters.sub_category_id;
            if (filters.search) params.search = filters.search;

            const response = await api.get('/exercises', { params });
            setExercises(response.data);
            setError('');
        } catch (err) {
            console.error('Error fetching exercises:', err);
            setError('Failed to load exercises');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (exerciseId) => {
        if (!window.confirm('Are you sure you want to delete this exercise?')) {
            return;
        }

        try {
            await api.delete(`/exercises/${exerciseId}`);
            await fetchExercises();
        } catch (err) {
            console.error('Error deleting exercise:', err);
            setError(err.response?.data?.detail || 'Failed to delete exercise');
        }
    };

    const handleToggleSelect = (exerciseId) => {
        setSelectedIds(prev =>
            prev.includes(exerciseId)
                ? prev.filter(id => id !== exerciseId)
                : [...prev, exerciseId]
        );
    };

    const handleSelectAll = () => {
        if (selectedIds.length === exercises.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(exercises.map(ex => ex.id));
        }
    };

    const getCategoryName = (categoryId) => {
        const category = categories.find(c => c.id === categoryId);
        return category?.name || 'Unknown';
    };

    const getSubCategoryName = (categoryId, subCategoryId) => {
        const category = categories.find(c => c.id === categoryId);
        const subCategory = category?.sub_categories?.find(sc => sc.id === subCategoryId);
        return subCategory?.name || '-';
    };

    const selectedCategory = categories.find(c => c.id === parseInt(filters.category_id));
    const subCategories = selectedCategory?.sub_categories || [];

    if (loading && exercises.length === 0) {
        return <div className="loading">Loading exercises...</div>;
    }

    return (
        <div className="exercise-list">
            {/* Filters */}
            <div className="exercise-filters">
                <div className="filter-group">
                    <label>Language:</label>
                    <select
                        value={filters.language}
                        onChange={(e) => setFilters({ ...filters, language: e.target.value })}
                    >
                        <option value="all">All</option>
                        <option value="hi-IN">Hindi</option>
                        <option value="en-US">English</option>
                    </select>
                </div>

                <div className="filter-group">
                    <label>Category:</label>
                    <select
                        value={filters.category_id}
                        onChange={(e) => setFilters({ ...filters, category_id: e.target.value, sub_category_id: 'all' })}
                    >
                        <option value="all">All Categories</option>
                        {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>
                                {cat.name}
                            </option>
                        ))}
                    </select>
                </div>

                {subCategories.length > 0 && (
                    <div className="filter-group">
                        <label>SubCategory:</label>
                        <select
                            value={filters.sub_category_id}
                            onChange={(e) => setFilters({ ...filters, sub_category_id: e.target.value })}
                        >
                            <option value="all">All SubCategories</option>
                            {subCategories.map(subCat => (
                                <option key={subCat.id} value={subCat.id}>
                                    {subCat.name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="filter-group search-group">
                    <label>Search:</label>
                    <input
                        type="text"
                        value={filters.search}
                        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                        placeholder="Search by word..."
                    />
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            {/* Results count */}
            <div className="results-info">
                <span>{exercises.length} exercise{exercises.length !== 1 ? 's' : ''} found</span>
                {selectable && selectedIds.length > 0 && (
                    <span className="selected-count">{selectedIds.length} selected</span>
                )}
            </div>

            {/* Exercise Table */}
            {exercises.length === 0 ? (
                <div className="empty-state">
                    <p>No exercises found. Try adjusting your filters or create a new exercise.</p>
                </div>
            ) : (
                <div className="exercise-table-wrapper">
                    <table className="exercise-table">
                        <thead>
                            <tr>
                                {selectable && (
                                    <th>
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.length === exercises.length && exercises.length > 0}
                                            onChange={handleSelectAll}
                                        />
                                    </th>
                                )}
                                <th>Word</th>
                                <th>Language</th>
                                <th>Category</th>
                                <th>SubCategory</th>
                                <th>Phonemes</th>
                                <th>Image</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {exercises.map(exercise => (
                                <tr key={exercise.id}>
                                    {selectable && (
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.includes(exercise.id)}
                                                onChange={() => handleToggleSelect(exercise.id)}
                                            />
                                        </td>
                                    )}
                                    <td className="word-cell">
                                        <strong>{exercise.word_text}</strong>
                                    </td>
                                    <td>
                                        <span className="language-badge">
                                            {exercise.language === 'hi-IN' ? '🇮🇳 Hindi' : '🇺🇸 English'}
                                        </span>
                                    </td>
                                    <td>{getCategoryName(exercise.category_id)}</td>
                                    <td>{getSubCategoryName(exercise.category_id, exercise.sub_category_id)}</td>
                                    <td className="phonemes-cell">
                                        {exercise.target_phonemes?.length || 0} phoneme{exercise.target_phonemes?.length !== 1 ? 's' : ''}
                                    </td>
                                    <td>
                                        {exercise.image_url && (
                                            <div className="thumbnail-wrapper">
                                                <img
                                                    src={exercise.image_url.startsWith('http')
                                                        ? exercise.image_url
                                                        : `${BACKEND_URL}${exercise.image_url}`}
                                                    alt={exercise.word_text}
                                                    className="exercise-thumbnail"
                                                />
                                            </div>
                                        )}
                                    </td>
                                    <td className="actions-cell">
                                        <button
                                            onClick={() => onEdit(exercise)}
                                            className="btn btn-sm btn-secondary"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(exercise.id)}
                                            className="btn btn-sm btn-danger"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ExerciseList;
